import type { ServiceAmountTypes, IService } from './IService'
import type { IAddress } from './IAddress'
import type { IUser } from './IUser'
import type { ITarget } from './ITarget'
import type { ICoupon } from './ICoupon'
import type { IClientCustomer, IClientCustomerSocial, ICustomerContacts } from './ICustomer'
import type { UUID } from './UUID'
import type { PaymentStatus } from './IPayment'
import type { BookingPriceSnapshot } from './BookingPriceSnapshot'
import type { IClientRentalItemRequest, IInventoryRentUnit } from './IInventory'

export enum EPaymentMethod {
  CASH = 'cash',
  TERMINAL = 'terminal',
  PAYMENT_ACCOUNT = 'paymentAccount',
  OTHER = 'other',
  ONLINE_PAYMENT = 'onlinePayment',
  QR_CODE_PAYMENT = 'QrCodePayment',
}

export enum EPaymentType {
  Prepayment = 'prepayment',
  Additional = 'additional',
  Surcharge = 'surcharge',
}

export enum OrderStatuses {
  Created = 'created',
  Confirmed = 'confirmed',
  Processed = 'processed',
  NoAnswer = 'noAnswer',
  Canceled = 'canceled',
  DidNotCome = 'didNotCome',
}

export enum OrderStatusesOther {
  Inprocess = 'inprocess',
  Completed = 'completed',
  TechnicalReservation = 'technicalReservation',
}

export const OrderStatusesAll = { ...OrderStatusesOther, ...OrderStatuses }
export type OrderStatusesAll = (typeof OrderStatusesAll)[keyof typeof OrderStatusesAll]

export enum OrderPaymentStatuses {
  InProcess = 'inProcess',
  NotPaid = 'notPaid',
  Paid = 'paid',
  Prepayment = 'prepayment',
  Cancel = 'cancel',
  Returned = 'returned',
  Rejected = 'rejected',
  PartReturned = 'partReturned',
  WithoutPayment = 'withoutPayment',
}

export interface IOrderPayment {
  id: number
  _id?: string
  amount: number
  currency?: string
  orderId: string
  type: EPaymentType
  method?: EPaymentMethod
  format?: string
  comment?: string
  providerPaymentURL?: string
  sbpQrLink?: string
  providerPaymentStatusComment?: string
  providerPaymentStatusCode?: string
  providerPaymentStatus?: string
  providerPaymentId?: string
  providerPaymentErrorCode?: string
  provider?: string
  status?: PaymentStatus
  refundedAmount?: number
  beforeRefundedAmount?: number
  created: Date
}

export interface IOrderService {
  id: number
  title: string
  price: number
  type: string
  seatsAmount: number
  count: number
  service: IService & { bookingPriceSnapshot?: BookingPriceSnapshot }
  additionalServices: IOrderAdditionalService[]
  freeAdditionalServices: IOrderFreeAdditionalServiceEntity[]
  target: ITarget
  targetId: number
}

export interface IOrderAdditionalService {
  id: number
  _id?: string
  title: string
  additionalType: string
  paymentTypes: string
  duration: number
  count: number
  price: number
  discount?: number
  discountType?: 'amount' | 'percent'
  service: IService
}

export interface IOrderFreeAdditionalServiceEntity {
  id: number
  _id?: string
  title: string
  additionalType: string
  paymentTypes: string
  duration: number
  count: number
  price: number
  service: IService
}

export interface IGoogleCalendarEntity {
  id: UUID
  googleCalendarId: string
  clientId: number
  googleAuthId: string
  title: string
  description?: string
  timezone: string
  accessRole: string
  primary: boolean
  eventsLastSyncAt?: Date
  createdAt: Date
  updatedAt: Date
  deletedAt?: Date
}

export interface IGoogleEventEntity {
  id: string
  description?: string
  googleCalendarId?: string
  googleCalendar?: IGoogleCalendarEntity
}

export interface ClientCustomStatusPayload {
  title: string
  color: string
  sortPosition?: number
}

export interface IOrderCustomStatus {
  id: number
  clientId: number
  client: Record<string, unknown>
  title: string
  color: string
  sortPosition: number
  createdAt: string
  updatedAt: string
  deletedAt: unknown
}

export interface IOrderItem {
  id?: number
  serviceType?: string
  role?: 'MAIN' | 'ADDON'
  title?: string
  unitPrice?: number
  price?: number
  serviceId: number
  quantity: number
  unitIds?: number[]
  resources?: {
    type: 'INVENTORY'
    id: number
    quantity: number
    units?: IInventoryRentUnit[]
  }[]
}

export interface IOrder {
  id?: number
  uuid?: string
  orderSource: OrderSources
  address: Omit<Partial<IAddress>, 'id'> & { id: string }
  created?: Date
  updated?: Date | string
  deletedAt?: Date | null
  isCompleted?: boolean
  isTechnicalReservation?: boolean
  service: IOrderService
  comment?: string
  price?: number
  additionalServices: IOrderAdditionalService[]
  orderServices: IOrderService[]
  items?: IOrderItem[]
  status: OrderStatuses
  customStatusId?: number | null
  customStatus?: IOrderCustomStatus | null
  discount: number
  discountType: ServiceAmountTypes
  allDates: (Date | undefined)[]
  dateTimeStart?: Date
  dateTimeEnd?: Date
  prepayment: Pick<IOrderPayment, 'amount' | 'method'>
  surcharge: Pick<IOrderPayment, 'amount' | 'method'>
  additionalPayments: IOrderPayment[]
  payments: IOrderPayment[]
  paymentStatus?: OrderPaymentStatuses
  clientId?: number
  user?: IUser
  currency: string
  clientCustomer?: IClientCustomer
  clientCustomerId?: number
  customerContacts?: ICustomerContacts
  couponId?: string | null
  couponPrice?: number
  coupon?: ICoupon | null
  timeslotsId?: string | null
  googleEvent?: IGoogleEventEntity
}

export interface IClientOrderServiceAdditional {
  additionalType?: string
  paymentTypes?: string
  service: IService
  duration?: number
  count: number
  price?: number
  discount?: number
  discountType?: 'amount' | 'percent'
}

export interface IClientOrderService {
  id?: number
  target?: Pick<ITarget, 'id'>
  type?: string
  seatsAmount: number
  count: number
  duration: number
  service: IService & { bookingPriceSnapshot?: BookingPriceSnapshot }
  additionalServices: IClientOrderServiceAdditional[]
  freeAdditionalServices: IClientOrderServiceAdditional[]
  weekDays: string[]
}

export interface IClientOrder {
  id?: number
  uuid?: string
  created?: Date | string
  dateTimeStart?: Date
  dateTimeEnd?: Date
  datetimezone?: string
  comment: string
  price: number
  couponPrice?: number
  isTechnicalReservation?: boolean
  orderServices: IClientOrderService[]
  inventoryAddons?: IClientRentalItemRequest[]
  items?: IOrderItem[]
  address?: Pick<IAddress, 'id' | 'timezone' | 'isOnline'>
  payments: IOrderPayment[]
  discount?: number
  discountType?: 'amount' | 'percent'
  customer?: ICustomerContacts & { socials?: IClientCustomerSocial[] }
  couponCode?: string
  clientId?: number
  orderSource?: 'subclient'
  createFirstPaymentInBank?: boolean
  coupon?: Record<PropertyKey, any>
}

export enum OrderSources {
  Platform = 'platform',
  Widget = 'widget',
  Subclient = 'subclient',
  Google = 'googleCalendar',
}
